import styled, { createGlobalStyle } from 'styled-components'

import {
  accentRed,
  black,
  fieldBackground,
  linkBlue,
  mutedText,
  offWhite,
  pageBackground,
  placeholderText,
  sheetBackground,
} from 'styles/colors'

// Shared by every account screen that renders the dark card (sign-up, password
// reset). Keep the styling here rather than per screen so the pages can't drift
// apart visually.

// The app defaults to the light theme, which paints the body white. Repaint it
// so overscroll in a mobile webview doesn't flash white around the card.
export const AuthGlobalStyles = createGlobalStyle`
  body {
    background-color: ${pageBackground};
  }
`

export const Screen = styled.main`
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  min-height: 100vh;
  /* Falls back to 100vh above; dvh keeps the card clear of mobile browser chrome. */
  min-height: 100dvh;
  padding: 10px 12px calc(16px + env(safe-area-inset-bottom, 0px));
  background-color: ${pageBackground};
  font-family: 'Arial', sans-serif;
  color: ${offWhite};
`

export const Card = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 350px;
  padding: 28px;
  border-radius: 16px;
  background-color: ${sheetBackground};
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
`

export const LogoContainer = styled.div`
  svg {
    display: block;
    width: 87px;
    max-width: 100%;
    height: auto;
    margin-left: 6px;
    margin-top: 0px;
  }
`

export const Title = styled.h1`
  margin: 26px 0 0;
  font-size: 30px;
  font-weight: 500;
  text-align: center;
  color: ${offWhite};
  -webkit-font-smoothing: auto;
  -moz-osx-font-smoothing: auto;
`

export const Subtitle = styled.p`
  margin: 10px 0 0;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4;
  text-align: center;
  color: ${offWhite};
`

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
`

export const Field = styled.div`
  display: flex;
  flex-direction: column;

  & + & {
    margin-top: 14px;
  }
`

export const Label = styled.label`
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-align: center;
  color: ${offWhite};
  -webkit-font-smoothing: auto;
  -moz-osx-font-smoothing: auto;
`

export const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: none;
  border-radius: 14px;
  background-color: ${fieldBackground};
  color: ${offWhite};
  font-family: Arial, sans-serif;
  /* 16px keeps iOS from zooming the viewport on focus. */
  font-size: 16px;
  line-height: 48px;
  appearance: none;

  &::placeholder {
    color: ${placeholderText};
  }

  &:focus-visible {
    outline: 2px solid ${linkBlue};
    outline-offset: 2px;
  }
`

export const ErrorMessage = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  line-height: 1.4;
  text-align: center;
  color: ${accentRed};
`

export const LegalText = styled.p`
  margin: 14px 0 0;
  font-size: 13px;
  line-height: 1.4;
  text-align: center;
  color: ${mutedText};
`

export const LegalLink = styled.a`
  color: ${linkBlue};
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`

export const SubmitButton = styled.button`
  width: 100%;
  height: 48px;
  margin-top: 18px;
  border: none;
  border-radius: 24px;
  background-color: ${accentRed};
  color: ${black};
  font-family: Arial, sans-serif;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-font-smoothing: auto;
  -moz-osx-font-smoothing: auto;
`

export const SignInPrompt = styled.p`
  margin: 40px 0 0;
  font-size: 15px;
  line-height: 1.4;
  text-align: center;
  color: ${mutedText};
`

export const SignInLink = styled.a`
  color: ${linkBlue};
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`
